/**
 * useSWUpdate
 *
 * Detects when a new service worker has installed and is waiting to activate.
 * Returns a `updateReady` boolean and an `applyUpdate()` function that
 * instructs the waiting SW to skip waiting and reloads the page.
 *
 * Usage:
 *   const { updateReady, applyUpdate } = useSWUpdate();
 *   if (updateReady) {
 *     toast("New version available", { action: applyUpdate });
 *   }
 */

import { useState, useEffect, useCallback, useRef } from "react";

export function useSWUpdate() {
  const [waitingSW, setWaitingSW] = useState(null);
  const [updateReady, setUpdateReady] = useState(false);
  const registrationRef = useRef(null);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const markReady = (sw) => {
      setWaitingSW(sw);
      setUpdateReady(true);
    };

    const checkRegistration = (reg) => {
      registrationRef.current = reg;

      // Already waiting — deployment happened while this tab was open
      if (reg.waiting) {
        markReady(reg.waiting);
        return;
      }

      reg.addEventListener("updatefound", () => {
        const incoming = reg.installing;
        if (!incoming) return;
        incoming.addEventListener("statechange", () => {
          if (incoming.state === "installed" && navigator.serviceWorker.controller) {
            markReady(incoming);
          }
        });
      });
    };

    navigator.serviceWorker.getRegistration("/").then((reg) => {
      if (reg) checkRegistration(reg);
    });

    // Reload once the new SW takes control (fired after applyUpdate below).
    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    // ── Long-running tab support ──────────────────────────────────────────────
    // Browsers only check for a new SW on page load and on explicit reg.update()
    // calls. Tabs that stay open all day (admin dashboards, CRM panels) would
    // never see the new SW unless we poll. 60-second interval is a reasonable
    // balance: fast enough for same-day deployments, cheap enough to not matter.
    const pollInterval = setInterval(() => {
      if (registrationRef.current) registrationRef.current.update().catch(() => {});
    }, 60_000);

    // ── Visibility change support ─────────────────────────────────────────────
    // When a user returns to the tab after a deployment happened in the
    // background, trigger an immediate update check instead of waiting up to
    // 60 s for the next poll tick.
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const reg = registrationRef.current;
      if (!reg) return;
      reg.update().catch(() => {});
      // Also re-check whether a SW quietly entered waiting state while hidden.
      if (reg.waiting && !updateReady) markReady(reg.waiting);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      clearInterval(pollInterval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  // updateReady is read inside the closure but intentionally omitted from deps:
  // we only want this effect to run once (mount/unmount).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyUpdate = useCallback(() => {
    if (!waitingSW) return;
    waitingSW.postMessage({ type: "SKIP_WAITING" });
  }, [waitingSW]);

  return { updateReady, applyUpdate };
}
